import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Chip, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslate } from 'src/locales';
import ByzMuiDataGrid from 'src/components/core-components/mui/data-grid';
import { useAppSelector } from 'src/app/hooks';
import surgeryService, {
  useInpatientOperations,
  useInpatientRequestProcess,
} from 'src/api/surgery/surgeryService';
import {
  InpatientRequestFilterDto,
  InpatientRequestResponseDto,
} from 'src/api/surgery/surgeryServiceDtos';
import {
  createOperationsColumns,
  createPostSurgerySteps,
  createProcessHistoryColumns,
  createProcessSteps,
  createSurgeryColumns,
  getStatusNameById,
  getStatusStepNumber,
  groupProcessHistoryByDate,
} from './helpers';
const { OperatingRoomConfirmDialog } = require('./components/OperatingRoomConfirmDialog');
const { OperatingRoomFilters } = require('./components/OperatingRoomFilters');
const { OperatingRoomFlowSection } = require('./components/OperatingRoomFlowSection');
const { OperatingRoomSidePanels } = require('./components/OperatingRoomSidePanels');

type SurgeryData = InpatientRequestResponseDto;

function OperatingRoomProcedures() {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { user, selectedOfficeId } = useAppSelector((state) => state.auth);

  const [showSurgeries, setShowSurgeries] = useState(true);
  const [showProcessDetails, setShowProcessDetails] = useState(true);
  const [selectedSurgery, setSelectedSurgery] = useState<SurgeryData | null>(null);
  const [selectedSurgeryOperations, setSelectedSurgeryOperations] = useState<any[]>([]);
  const [operationsLoading, setOperationsLoading] = useState(false);
  const [processHistory, setProcessHistory] = useState<any[]>([]);
  const [processHistoryLoading, setProcessHistoryLoading] = useState(false);
  const [surgeryFlowLoading, setSurgeryFlowLoading] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<{ type: 'ertelendi' | 'iptal'; stepId: number } | null>(null);
  const [surgeryFlowState, setSurgeryFlowState] = useState(5);
  const [searchType, setSearchType] = useState<'dosya-tc' | 'protokol'>('dosya-tc');
  const [searchValue, setSearchValue] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [loading, setLoading] = useState(false);
  const [surgeryData, setSurgeryData] = useState<SurgeryData[]>([]);
  const [showNoReport, setShowNoReport] = useState(false);
  const [showDischarged, setShowDischarged] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<number>(0);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [localSelectedOfficeId, setLocalSelectedOfficeId] = useState<number | null>(selectedOfficeId || null);

  useEffect(() => {
    if (selectedOfficeId) {
      setLocalSelectedOfficeId(selectedOfficeId);
    }
  }, [selectedOfficeId]);

  const currentSelectedOfficeId = localSelectedOfficeId ?? selectedOfficeId;
  const selectedOffice = user?.UserAssPriviliges?.find((office) => office.ID === currentSelectedOfficeId);
  const offId = selectedOffice?.OFF_ID;
  const normalizedUserOffices =
    user?.UserAssPriviliges?.map((office) => ({
      ID: office.ID ?? undefined,
      OFFICE_NAME: office.OFFICE_NAME ?? undefined,
    })) ?? undefined;
  const normalizedSelectedOffice = selectedOffice
    ? {
        ID: selectedOffice.ID ?? undefined,
        OFFICE_NAME: selectedOffice.OFFICE_NAME ?? undefined,
      }
    : undefined;

  const statusOptions = useMemo(
    () => [
      { value: 0, label: t('surgery.filters.all') },
      { value: 1645, label: t('surgery.filters.pending') },
      { value: 1655, label: t('surgery.filters.preopCompleted') },
      { value: 1650, label: t('surgery.filters.called') },
      { value: 1660, label: t('surgery.filters.sent') },
      { value: 1665, label: t('surgery.filters.accepted') },
      { value: 1670, label: t('surgery.filters.identityVerified') },
      { value: 1675, label: t('surgery.filters.onOperatingTable') },
      { value: 1680, label: t('surgery.filters.inSurgery') },
      { value: 1685, label: t('surgery.filters.surgeryEnded') },
      { value: 1690, label: t('surgery.filters.postponed') },
      { value: 1695, label: t('surgery.filters.cancelled') },
      { value: 1700, label: t('surgery.filters.inRecovery') },
      { value: 1705, label: t('surgery.filters.sentToWard') },
      { value: 1715, label: t('surgery.filters.sentToAdmissionUnit') },
    ],
    [t]
  );

  const processSteps = useMemo(() => createProcessSteps(t, surgeryFlowState), [t, surgeryFlowState]);
  const postSurgerySteps = useMemo(() => createPostSurgerySteps(t, surgeryFlowState), [t, surgeryFlowState]);
  const columns = useMemo(() => createSurgeryColumns(t), [t]);
  const operationsColumns = useMemo(() => createOperationsColumns(t), [t]);
  const processHistoryColumns = useMemo(() => createProcessHistoryColumns(t), [t]);

  const handleListele = useCallback(async () => {
    if (!startDate || !endDate || !offId) {
      console.error('Required fields missing', { startDate, endDate, offId });
      return;
    }

    setLoading(true);

    try {
      const filterData: InpatientRequestFilterDto = {
        StartDate: startDate.toISOString(),
        EndDate: endDate.toISOString(),
        OffId: offId,
        CheckReport: showNoReport ? '1' : '0',
        PsId2: selectedStatus,
        PtId2: 0,
        UpnOrIdentityNo: searchType === 'dosya-tc' ? searchValue : '0',
        ProtocolNo: searchType === 'protokol' ? Number(searchValue) : 0,
        IsDischarged: showDischarged ? 1 : 0,
      };

      const response = await surgeryService.inpatientRequest.getInpatientRequests(filterData);
      setSurgeryData(response?.data || []);

      if (searchValue.trim()) {
        const searchTypeText =
          searchType === 'dosya-tc' ? t('surgery.filters.fileTcNo') : t('surgery.filters.protocolNo');
        enqueueSnackbar(`${searchTypeText}: ${searchValue} ${t('surgery.messages.searchCompleted')}`, {
          variant: 'success',
          autoHideDuration: 3000,
        });
      }
    } catch {
      enqueueSnackbar(t('surgery.messages.errorDuringSearch'), {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      setLoading(false);
      setSearchValue('');
    }
  }, [
    endDate,
    enqueueSnackbar,
    offId,
    searchType,
    searchValue,
    selectedStatus,
    showDischarged,
    showNoReport,
    startDate,
    t,
  ]);

  useEffect(() => {
    if (!startDate) return;

    const nextDay = new Date(startDate);
    nextDay.setDate(nextDay.getDate() + 1);

    if (!endDate || endDate <= startDate) {
      setEndDate(nextDay);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!autoRefresh || loading) return undefined;

    const intervalId = setInterval(() => {
      handleListele();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [autoRefresh, handleListele, loading]);

  useEffect(() => {
    if (!loading) return;

    setSelectedSurgery(null);
    setSelectedSurgeryOperations([]);
    setProcessHistory([]);
    setSurgeryFlowState(0);
  }, [loading]);

  const operationsQuery = useInpatientOperations(selectedSurgery?.id || 0);
  const processQuery = useInpatientRequestProcess(selectedSurgery?.id || 0);

  useEffect(() => {
    if (operationsQuery.data) {
      setSelectedSurgeryOperations(operationsQuery.data.data || []);
      setOperationsLoading(false);
    }

    if (operationsQuery.isLoading) {
      setOperationsLoading(true);
    }
  }, [operationsQuery.data, operationsQuery.isLoading]);

  useEffect(() => {
    if (processQuery.data) {
      setProcessHistory(groupProcessHistoryByDate(processQuery.data.data || []));
      setProcessHistoryLoading(false);
    }

    if (processQuery.isLoading) {
      setProcessHistoryLoading(true);
    }
  }, [processQuery.data, processQuery.isLoading]);

  const getLastStatusDateTime = useCallback(
    (statusId: number) => {
      if (!processHistory?.length) return '';

      const rows = processHistory.filter(
        (row: any) => !row.isHeader && (row.stateId === statusId || row.StateId === statusId)
      );
      if (!rows.length) return '';

      const sorted = [...rows].sort((a: any, b: any) => {
        const bTime = new Date(b.originalTime || b.Ptime || b.ptime || b.time).getTime();
        const aTime = new Date(a.originalTime || a.Ptime || a.ptime || a.time).getTime();
        return bTime - aTime;
      });

      const dateString = sorted[0].originalTime || sorted[0].Ptime || sorted[0].ptime;
      if (!dateString) return '';

      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      return date.toLocaleString('tr-TR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    [processHistory]
  );

  const handleSurgeryFlowButton = useCallback(
    async (stepId: number) => {
      if (!selectedSurgery) {
        enqueueSnackbar(t('surgery.messages.selectPatientFirst'), {
          variant: 'warning',
          autoHideDuration: 3000,
        });
        return;
      }

      const previousStatusId = selectedSurgery.statusid;
      const previousSurgeryFlowState = surgeryFlowState;

      const checkStatus = await surgeryService.inpatientRequest.getCheckInpatientRequestStatus(selectedSurgery.id);
      if (checkStatus.data !== selectedSurgery.statusid) {
        enqueueSnackbar(t('surgery.messages.statusOutdated'), {
          variant: 'info',
          autoHideDuration: 3000,
        });
        setSurgeryFlowState(checkStatus.data);
        setSelectedSurgery((prev) => (prev ? { ...prev, statusid: checkStatus.data } : null));
        return;
      }

      setSurgeryFlowLoading(true);

      try {
        await surgeryService.inpatientRequest.postCreateInpatientRequestProcess({
          IrId: selectedSurgery.id,
          StateId: stepId,
          Ptime: new Date().toISOString(),
          IctId: null,
          IctNote: null,
        });

        const buttonName = getStatusNameById(stepId, processSteps, postSurgerySteps);

        enqueueSnackbar(`${t('surgery.messages.surgeryFlowUpdated')} "${buttonName}"`, {
          variant: 'success',
          autoHideDuration: 3000,
        });

        setSelectedSurgery((prev) => (prev ? { ...prev, statusid: stepId } : null));
        setSurgeryFlowState(stepId);

        setSurgeryData((prevData) =>
          prevData.map((surgery) =>
            surgery.id === selectedSurgery.id
              ? { ...surgery, statusid: stepId, processstatusname2: getStatusNameById(stepId, processSteps, postSurgerySteps) }
              : surgery
          )
        );

        if (selectedSurgery && processQuery.data) {
          setProcessHistory(groupProcessHistoryByDate(processQuery.data.data || []));
        }
      } catch {
        setSelectedSurgery((prev) => (prev ? { ...prev, statusid: previousStatusId } : null));
        setSurgeryFlowState(previousSurgeryFlowState);

        setSurgeryData((prevData) =>
          prevData.map((surgery) =>
            surgery.id === selectedSurgery.id
              ? {
                  ...surgery,
                  statusid: previousStatusId,
                  processstatusname2: getStatusNameById(previousStatusId || 0, processSteps, postSurgerySteps),
                }
              : surgery
          )
        );

        enqueueSnackbar(t('surgery.messages.errorUpdatingFlow'), {
          variant: 'error',
          autoHideDuration: 3000,
        });
      } finally {
        setSurgeryFlowLoading(false);
      }
    },
    [enqueueSnackbar, postSurgerySteps, processQuery.data, processSteps, selectedSurgery, surgeryFlowState, t]
  );

  const handleConfirmAction = useCallback(() => {
    if (pendingAction) {
      handleSurgeryFlowButton(pendingAction.stepId);
    }
    setShowConfirmModal(false);
    setPendingAction(null);
  }, [handleSurgeryFlowButton, pendingAction]);

  const handleCancelAction = useCallback(() => {
    setShowConfirmModal(false);
    setPendingAction(null);
  }, []);

  const handleRowClick = useCallback(
    async (params: any) => {
      try {
        if (selectedSurgery?.id === params.row.id) return;

        setSelectedSurgery(params.row);
        setSelectedSurgeryOperations([]);
        setProcessHistory([]);
        setSurgeryFlowState(params.row.statusid || 0);
      } catch {
        enqueueSnackbar(t('surgery.messages.errorFetchingPatientData'), {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    },
    [enqueueSnackbar, selectedSurgery?.id, t]
  );

  return (
    <Box sx={{ p: 2, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <OperatingRoomFilters
        t={t}
        userOffices={normalizedUserOffices}
        selectedOffice={normalizedSelectedOffice}
        startDate={startDate}
        endDate={endDate}
        searchType={searchType}
        searchValue={searchValue}
        selectedStatus={selectedStatus}
        statusOptions={statusOptions}
        autoRefresh={autoRefresh}
        showNoReport={showNoReport}
        showDischarged={showDischarged}
        showSurgeries={showSurgeries}
        showProcessDetails={showProcessDetails}
        loading={loading}
        onOfficeChange={setLocalSelectedOfficeId}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onSearchTypeChange={setSearchType}
        onSearchValueChange={setSearchValue}
        onSearch={handleListele}
        onStatusChange={setSelectedStatus}
        onAutoRefreshChange={setAutoRefresh}
        onShowNoReportChange={setShowNoReport}
        onShowDischargedChange={setShowDischarged}
        onShowSurgeriesChange={setShowSurgeries}
        onShowProcessDetailsChange={setShowProcessDetails}
      />

      <OperatingRoomFlowSection
        t={t}
        selectedSurgery={selectedSurgery}
        loading={loading}
        operationsLoading={operationsLoading}
        processHistoryLoading={processHistoryLoading}
        surgeryFlowLoading={surgeryFlowLoading}
        surgeryFlowState={surgeryFlowState}
        processSteps={processSteps}
        postSurgerySteps={postSurgerySteps}
        getStatusStepNumber={getStatusStepNumber}
        getLastStatusDateTime={getLastStatusDateTime}
        onFlowStepClick={handleSurgeryFlowButton}
        onRequestConfirmAction={(action: { type: 'ertelendi' | 'iptal'; stepId: number }) => {
          setPendingAction(action);
          setShowConfirmModal(true);
        }}
      />

      <Box sx={{ flex: 1, display: 'flex', gap: 2, minHeight: 0 }}>
        <Box sx={{ flex: 3.2, minWidth: 0 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('surgery.tables.surgeryList')}
              </Typography>
              <ByzMuiDataGrid
                columns={columns}
                rows={surgeryData}
                loading={loading}
                pageSize={15}
                getRowId={(row) => row.id}
                onRowClick={handleRowClick}
                sx={{
                  height: 'calc(100vh - 300px)',
                  minHeight: 650,
                  fontSize: '0.74rem',
                  '& .MuiDataGrid-cell': { fontSize: '0.74rem', py: 0.5 },
                  '& .MuiDataGrid-columnHeader': { fontSize: '0.74rem', py: 0.5 },
                  '& .MuiTablePagination-root': { fontSize: '0.74rem' },
                }}
              />
            </CardContent>
          </Card>
        </Box>

        <OperatingRoomSidePanels
          t={t}
          showSurgeries={showSurgeries}
          showProcessDetails={showProcessDetails}
          selectedSurgery={selectedSurgery}
          selectedSurgeryOperations={selectedSurgeryOperations}
          processHistory={processHistory}
          operationsLoading={operationsLoading}
          processHistoryLoading={processHistoryLoading}
          operationsColumns={operationsColumns}
          processHistoryColumns={processHistoryColumns}
        />
      </Box>

      <OperatingRoomConfirmDialog
        t={t}
        open={showConfirmModal}
        pendingAction={pendingAction}
        onCancel={handleCancelAction}
        onConfirm={handleConfirmAction}
      />
    </Box>
  );
}

export default OperatingRoomProcedures;
